import {
  Alert, Button, Chip, Dialog, DialogActions, DialogContent, Stack, Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import axios from "axios";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import type { ApiProblem } from "@/api/client";
import type { ConfusionType, LasaSeverity } from "@/api/endpoints";

/** One unconfirmed pair from a 422 LASA_CONFIRMATION_REQUIRED response. */
export interface ServerLasaConflict {
  itemId: string; itemSku: string; itemName: string;
  partnerItemId: string; partnerSku: string; partnerName: string;
  confusionType: ConfusionType; severity: LasaSeverity; notes?: string | null;
}

/**
 * Pull the conflicts out of a rejected dispense. Returns null for any other error, so callers can
 * chain it ahead of their normal error handling.
 */
export function lasaConflictsFrom(err: unknown): ServerLasaConflict[] | null {
  if (!axios.isAxiosError<ApiProblem>(err)) return null;
  if (err.response?.status !== 422) return null;
  if (err.response.data?.code !== "LASA_CONFIRMATION_REQUIRED") return null;
  const payload = err.response.data.errors as { conflicts?: ServerLasaConflict[] } | undefined;
  return payload?.conflicts?.length ? payload.conflicts : null;
}

/**
 * The blocking confirmation for High-severity look-alike / sound-alike items, shown wherever stock
 * is dispensed — pharmacy counter, ward issue, OT consumption. The server refuses the dispense
 * until the operator confirms, and records who confirmed what, so this dialog is the gate rather
 * than a courtesy warning.
 */
export function LasaConfirmDialog({
  conflicts, onAccept, onCancel, busy,
}: {
  conflicts: ServerLasaConflict[] | null;
  /** Called with the item ids to acknowledge — pass these back on the retry. */
  onAccept: (itemIds: string[]) => void;
  onCancel: () => void;
  busy?: boolean;
}) {
  const open = !!conflicts?.length;
  const itemIds = [...new Set((conflicts ?? []).map(c => c.itemId))];

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth data-testid="lasa-confirm">
      <ClosableDialogTitle onClose={onCancel}>
        <Stack direction="row" spacing={1} alignItems="center">
          <WarningAmberIcon color="error" /> <span>Confirm the correct item</span>
        </Stack>
      </ClosableDialogTitle>
      <DialogContent dividers>
        <Alert severity="error" sx={{ mb: 2 }}>
          {itemIds.length === 1
            ? <>This item is a high-risk look-alike / sound-alike.</>
            : <>These items are high-risk look-alike / sound-alike.</>}
          {" "}Check the pack before it leaves the shelf.
        </Alert>
        <Stack spacing={2}>
          {itemIds.map(id => {
            const forItem = (conflicts ?? []).filter(c => c.itemId === id);
            const head = forItem[0];
            return (
              <Stack key={id} spacing={0.5}>
                <Typography variant="subtitle2">
                  <b>{head.itemSku}</b> · {head.itemName}
                </Typography>
                <Typography variant="body2" color="text.secondary">Can be confused with:</Typography>
                {forItem.map(c => (
                  <Typography key={c.partnerItemId} variant="body2">
                    • <b>{c.partnerSku}</b> · {c.partnerName}
                    <Chip size="small" color="error" label={c.severity} sx={{ ml: 0.5 }} />
                    {c.notes ? <Typography variant="caption" color="text.secondary"> — {c.notes}</Typography> : null}
                  </Typography>
                ))}
              </Stack>
            );
          })}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
          Confirming is recorded against your user for audit.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onCancel} data-testid="lasa-confirm-cancel">
          Go back and change
        </Button>
        <Button variant="contained" color="error" disabled={busy}
          onClick={() => onAccept(itemIds)} data-testid="lasa-confirm-accept">
          Yes, these are correct
        </Button>
      </DialogActions>
    </Dialog>
  );
}
